import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const OwnerOrders = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filter States
    const [filterDate, setFilterDate] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterTimeSlot, setFilterTimeSlot] = useState('');
    const [filterAssignedType, setFilterAssignedType] = useState('');

    // Reassign Modal State
    const [showReassignModal, setShowReassignModal] = useState(false);
    const [reassignOrder, setReassignOrder] = useState(null);
    const [reassigning, setReassigning] = useState(false);

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            let url = `https://rotica.in/get_all_orders.php?`;

            // Append filters
            const params = new URLSearchParams();
            if (filterDate) params.append('date', filterDate);
            if (filterStatus) params.append('status', filterStatus);
            if (filterTimeSlot) params.append('time_slot', filterTimeSlot);
            if (filterAssignedType) params.append('assigned_type', filterAssignedType);

            const response = await axios.get(url + params.toString());

            if (response.data.success) {
                setOrders(response.data.orders);
            } else {
                setError(response.data.message || "Failed to fetch orders.");
            }
        } catch (err) {
            console.error(err);
            setError("Network Error: Could not fetch orders.");
        } finally {
            setLoading(false);
        }
    }, [filterDate, filterStatus, filterTimeSlot, filterAssignedType]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const clearFilters = () => {
        setFilterDate('');
        setFilterStatus('');
        setFilterTimeSlot('');
        setFilterAssignedType('');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return date.toLocaleDateString('en-IN', options);
    };

    // Handle reassign button click - open confirmation modal
    const handleReassignClick = (e, order) => {
        e.stopPropagation(); // Prevent row click navigation
        setReassignOrder(order);
        setShowReassignModal(true);
    };

    // Confirm reassignment
    const confirmReassign = async () => {
        if (!reassignOrder) return;

        setReassigning(true);
        try {
            const formData = new FormData();
            formData.append('order_id', reassignOrder.id);
            // Assuming the backend needs to know who to skip in round robin, 
            // if that's how reassign logic works. Passing current assigned entity.
            formData.append('skip_entity_id', reassignOrder.assigned_id);
            formData.append('skip_entity_type', reassignOrder.assigned_type);

            const response = await axios.post('https://rotica.in/reassign_order.php', formData);

            if (response.data.success) {
                alert(`Order #${reassignOrder.id} reassigned successfully to ${response.data.assigned_type} #${response.data.assigned_id}`);
                fetchOrders(); // Refresh the orders list
            } else {
                alert('Reassignment failed: ' + response.data.message);
            }
        } catch (err) {
            console.error(err);
            alert('Network error during reassignment');
        } finally {
            setReassigning(false);
            setShowReassignModal(false);
            setReassignOrder(null);
        }
    };

    return (
        <div className="dashboard-container">
            <div className="main-content">
                <header className="page-header">
                    <h1>All Orders</h1>
                    <p className="subtitle">System-wide order management</p>
                </header>

                <div className="stats-cards">
                    <div className="stat-card">
                        <h3>Total Orders</h3>
                        <p>{orders.length}</p>
                    </div>
                    <div className="stat-card">
                        <h3>Assigned to Distributors</h3>
                        <p>{orders.filter(o => o.assigned_type === 'distributor').length}</p>
                    </div>
                    <div className="stat-card">
                        <h3>Assigned to Manufacturers</h3>
                        <p>{orders.filter(o => o.assigned_type === 'manufacturer').length}</p>
                    </div>
                    <div className="stat-card">
                        <h3>Unassigned</h3>
                        <p>{orders.filter(o => !o.assigned_type).length}</p>
                    </div>
                </div>

                <div className="orders-section">
                    {/* Filters Bar */}
                    <div className="filters-bar">
                        <div className="filter-group">
                            <label>Date</label>
                            <input
                                type="date"
                                className="filter-input"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                            />
                        </div>

                        <div className="filter-group">
                            <label>Status</label>
                            <select
                                className="filter-select"
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                            >
                                <option value="">All Statuses</option>
                                <option value="assigned">Assigned</option>
                                <option value="unfulfilled">Unfulfilled</option>
                                <option value="Delivered">Delivered</option>
                                <option value="Delayed">Delayed</option>
                                <option value="Cancelled">Cancelled</option>
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Time Slot</label>
                            <select
                                className="filter-select"
                                value={filterTimeSlot}
                                onChange={(e) => setFilterTimeSlot(e.target.value)}
                            >
                                <option value="">All Slots</option>
                                <option value="7-10 AM">7-10 AM</option>
                                <option value="12-3 PM">12-3 PM</option>
                                <option value="6-9 PM">6-9 PM</option>
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Assigned To</label>
                            <select
                                className="filter-select"
                                value={filterAssignedType}
                                onChange={(e) => setFilterAssignedType(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="distributor">Distributors</option>
                                <option value="manufacturer">Manufacturers</option>
                            </select>
                        </div>

                        <button className="clear-btn" onClick={clearFilters}>
                            Clear Filters
                        </button>
                    </div>

                    {loading && <p>Loading orders...</p>}
                    {error && <p className="error-text">{error}</p>}

                    {!loading && !error && (
                        <div className="table-wrapper">
                            <table className="orders-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Customer</th>
                                        <th>Items</th>
                                        <th>Assigned To</th>
                                        <th>Delivery Info</th>
                                        <th>Status</th>
                                        <th>Created</th>
                                        <th>Reassign</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.length > 0 ? (
                                        orders.map(order => (
                                            <tr key={order.id} onClick={() => navigate(`/order/${order.id}`)} style={{ cursor: 'pointer' }}>
                                                <td>#{order.id}</td>
                                                <td>
                                                    <div className="customer-info">
                                                        <span className="customer-name">{order.customer_name}</span>
                                                        <span className="customer-phone">{order.contact_no}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="item-count-badge">
                                                        {order.items ? order.items.length : 0} Items
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                        <span className={`assigned-badge ${order.assigned_type || 'unassigned'}`}>
                                                            {order.assigned_type ? order.assigned_type.charAt(0).toUpperCase() + order.assigned_type.slice(1) : 'Unassigned'}
                                                        </span>
                                                        <span style={{ fontSize: '12px', color: '#666' }}>
                                                            {order.assigned_name || '-'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: '13px' }}>
                                                        <div><strong>Time:</strong> {order.delivery_time}</div>
                                                        <div style={{ color: '#86868b', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {order.address_text}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`status-badge ${order.status?.toLowerCase().replace(/\s/g, '-')}`}>
                                                        {order.status || 'Pending'}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: '12px', color: '#86868b' }}>
                                                    {formatDate(order.created_at)}
                                                </td>
                                                <td>
                                                    {order.assigned_type && (
                                                        <button
                                                            className="reassign-btn"
                                                            onClick={(e) => handleReassignClick(e, order)}
                                                        >
                                                            Reassign
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" style={{ textAlign: "center", padding: "40px" }}>
                                                No orders found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Reassign Confirmation Modal */}
            {showReassignModal && (
                <div className="modal-overlay" onClick={() => setShowReassignModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Confirm Reassignment</h2>
                        <p>
                            Are you sure you want to reassign Order <strong>#{reassignOrder?.id}</strong>?
                        </p>
                        <p style={{ fontSize: '14px', color: '#666' }}>
                            Currently assigned to: <strong>{reassignOrder?.assigned_type}</strong> ({reassignOrder?.assigned_name || 'Unknown'})
                        </p>
                        <p style={{ fontSize: '14px', color: '#666' }}>
                            The order will be reassigned to a different distributor/manufacturer.
                        </p>
                        <div className="modal-actions">
                            <button
                                className="cancel-btn"
                                onClick={() => setShowReassignModal(false)}
                                disabled={reassigning}
                            >
                                Cancel
                            </button>
                            <button
                                className="confirm-btn"
                                onClick={confirmReassign}
                                disabled={reassigning}
                            >
                                {reassigning ? 'Reassigning...' : 'Confirm Reassign'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OwnerOrders;
